import React from "react";
import { FormProvider, SubmitHandler } from "react-hook-form";
import { Button, Callout, Dialog, Form, KeyValueList, Toast } from "@sscs/ui";
import { bytesToGB, convertToReadable, percentToGB } from "@sscs/utils";
import { PercentageSlider } from "Components";
import { MAX_PERCENTAGE, MIN_PERCENTAGE, apiErrorToast } from "Utils";
import { useSubmitAllocationsMutation } from "../../services/licensing.api";
import { ReallocateQuotasForm } from "../../types";
import styles from "./reallocateQuotas.module.scss";
import useQuotaSplit from "./useQuotaSplit";

const getPercentageHelperText = (
  percentage: number | null,
  value: number,
): string => {
  const bytes =
    percentage === null || isNaN(percentage) || isNaN(value)
      ? 0
      : percentToGB(percentage, value);
  return `(${convertToReadable(bytes)})`;
};

const getAllocationBytesFromPercentage = (
  percentage: number | null,
  totalCapacityBytes: number,
): number => {
  return percentage === null
    ? 0
    : Math.round((percentage / 100) * totalCapacityBytes);
};

const PORTAL_PERCENTAGE_FIELD_NAME = "portal_percentage";
const CLI_PERCENTAGE_FIELD_NAME = "cli_percentage";

type ReallocateQuotasProps = {
  totalCapacityBytes: number;
  portalCapacityBytes: number;
  portalUsageBytes: number;
  cliTotalAllocationBytes: number;
  isOpen: boolean;
  onClose: () => void;
};

const ReallocateQuotasContent = ({
  totalCapacityBytes,
  portalCapacityBytes,
  portalUsageBytes,
  cliTotalAllocationBytes,
  onClose,
}: Omit<ReallocateQuotasProps, "isOpen">): React.JSX.Element => {
  const [submitLimits] = useSubmitAllocationsMutation();

  const {
    formMethods,
    portalPercentage,
    cliPercentage,
    onCliPercentageChange,
    onPercentageSliderChange,
  } = useQuotaSplit({
    portalPercentageFieldName: PORTAL_PERCENTAGE_FIELD_NAME,
    cliPercentageFieldName: CLI_PERCENTAGE_FIELD_NAME,
    portalCapacityBytes,
    cliTotalAllocationBytes,
    totalCapacityBytes,
  });

  const {
    formState: { errors },
    handleSubmit,
  } = formMethods;

  const totalCapacityGB = bytesToGB(totalCapacityBytes);

  const portalAllocationGB = getPercentageHelperText(
    portalPercentage,
    totalCapacityGB,
  );
  const cliAllocationGB = getPercentageHelperText(
    cliPercentage,
    totalCapacityGB,
  );

  const onSubmit: SubmitHandler<ReallocateQuotasForm> = async (data) => {
    try {
      const portalAllocation = getAllocationBytesFromPercentage(
        data[PORTAL_PERCENTAGE_FIELD_NAME],
        totalCapacityBytes,
      );

      await submitLimits({
        body: {
          portal_allocation: portalAllocation,
          cli_allocation: totalCapacityBytes - portalAllocation,
        },
      }).unwrap();

      Toast.show({
        title: "Analysis capacity reallocated",
        type: "success",
      });
    } catch (error) {
      apiErrorToast(error, "Could not reallocate analysis capacity");
      return;
    }

    onClose();
  };

  return (
    <FormProvider {...formMethods}>
      <Form.Form onSubmit={handleSubmit(onSubmit)}>
        <Dialog.Header>
          <Dialog.Header.Title>
            Set CLI and Portal analysis capacity allocation
          </Dialog.Header.Title>
        </Dialog.Header>
        <Dialog.Body>
          <KeyValueList
            list={{
              "Total capacity": convertToReadable(totalCapacityBytes),
            }}
          />
          <Dialog.Section>
            <Dialog.Section.Content>
              <Form.FormRow>
                <Form.TextField
                  id="reallocate-quotas-portal-percentage"
                  label="Portal allocation (%)"
                  name={PORTAL_PERCENTAGE_FIELD_NAME}
                  type="number"
                  unit="%"
                  errors={errors}
                  helperText={portalAllocationGB}
                  inputProps={{
                    min: MIN_PERCENTAGE,
                    max: MAX_PERCENTAGE,
                    step: 1,
                  }}
                  className={styles.allocation}
                />
                <Form.TextField
                  id="reallocate-quotas-cli-percentage"
                  label="CLI allocation (%)"
                  type="number"
                  name={CLI_PERCENTAGE_FIELD_NAME}
                  errors={errors}
                  onChange={(event) =>
                    onCliPercentageChange(event.currentTarget.value)
                  }
                  unit="%"
                  helperText={cliAllocationGB}
                  inputProps={{
                    min: MIN_PERCENTAGE,
                    max: MAX_PERCENTAGE,
                    step: 1,
                    value: cliPercentage === null ? "" : cliPercentage,
                  }}
                  className={styles.allocation}
                />
              </Form.FormRow>
              <PercentageSlider
                value={portalPercentage}
                onChange={onPercentageSliderChange}
                firstLabel="Portal"
                secondLabel="CLI"
              />
              {getAllocationBytesFromPercentage(
                portalPercentage,
                totalCapacityBytes,
              ) < portalUsageBytes && (
                <Callout type="warning" title="Usage over desired capacity">
                  Current Portal usage ({convertToReadable(portalUsageBytes)})
                  exceeds desired Portal capacity {portalAllocationGB}
                </Callout>
              )}
              {getAllocationBytesFromPercentage(
                cliPercentage,
                totalCapacityBytes,
              ) < cliTotalAllocationBytes && (
                <Callout type="error" title="Invalid allocation">
                  Current total CLI allocations (
                  {convertToReadable(cliTotalAllocationBytes)}) exceeds desired
                  CLI capacity {cliAllocationGB}
                </Callout>
              )}
            </Dialog.Section.Content>
          </Dialog.Section>
        </Dialog.Body>
        <Dialog.Footer>
          <Dialog.Footer.Actions>
            <Button color="secondary" onClick={onClose}>
              Close
            </Button>
            <Button type="submit" color="primary">
              Set allocation
            </Button>
          </Dialog.Footer.Actions>
        </Dialog.Footer>
      </Form.Form>
    </FormProvider>
  );
};

const ReallocateQuotas = ({
  isOpen,
  ...props
}: ReallocateQuotasProps): React.JSX.Element => (
  <Dialog.Frame isOpen={isOpen} onClose={props.onClose}>
    <ReallocateQuotasContent {...props} />
  </Dialog.Frame>
);

export default ReallocateQuotas;
